import { useEffect, useState } from 'react'
import { getDatabase, ref, onValue, set } from 'firebase/database'
import Button from '../components/Button'
import { useToast } from '../context/ToastContext'

const DEFAULT_TEMPLATES = {
  customerWelcome: {
    name: 'Customer Welcome',
    subject: 'Welcome to IHT Self-Service — Your Medipro API Portal',
    body: `Hi [name],

You've been set up with access to the Inspire Health Tech Self-Service portal. This is your hub for managing your Medipro API integration.

Here's what you can do:

- My Keys — View and copy your API keys and documentation access tokens
- API Usage — Run usage analytics reports and export data as CSV
- Medipro Browser — Test Medipro API endpoints interactively with live responses

To get started, visit: https://selfservice.inspireht.com

You can sign in using any of the following methods:

1. Email link (passwordless) — Enter your email on the sign-in page and choose "Sign in with email link." We'll send you a link — click it and you're in. No password needed.

2. Email and password — Click "Create an account" on the sign-in page, register with the email address we have on file for you, and set a password.

3. Google — If your email is associated with a Google account, click "Sign in with Google" for one-click access.

Note: Only the email address(es) we've authorized for your account will work. If you need to add additional users, let us know.

If you have any questions, reach out to us at support@inspireht.com.

Welcome aboard,
The Inspire Health Tech Team`
  },
  tokenAccess: {
    name: 'Token Access',
    subject: 'Your Medipro API Documentation Access Token',
    body: `Hello [name],

To access Inspire Health Tech's API documentation, go to:
https://docs.aipredict.medipro.inspireht.com/

Copy/Paste the following for UserName (no Password is required):

[token]

Please note that this token is set to expire [expireDate].

Thank you! Please let us know if you have any questions.

The Inspire Health Tech Team`
  }
}

const TemplateEditor = ({ templateKey, template, onSave }) => {
  const [subject, setSubject] = useState(template.subject)
  const [body, setBody] = useState(template.body)
  const [dirty, setDirty] = useState(false)
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    setSubject(template.subject)
    setBody(template.body)
    setDirty(false)
  }, [template])

  const handleSave = () => {
    onSave(templateKey, { ...template, subject, body })
    setDirty(false)
  }

  const handleCopy = () => {
    const text = `Subject: ${subject}\n\n${body}`
    navigator.clipboard.writeText(text)
    setCopied(true)
    setTimeout(() => setCopied(false), 1500)
  }

  return (
    <div className='bg-white border rounded-lg p-5 mb-4 shadow-sm'>
      <h3 className='text-lg font-bold mb-3'>{template.name}</h3>
      <label className='block text-sm font-bold mb-1'>Subject</label>
      <input
        type='text'
        className='border rounded p-2 w-full text-sm mb-3'
        value={subject}
        onChange={(e) => { setSubject(e.target.value); setDirty(true) }}
      />
      <label className='block text-sm font-bold mb-1'>Body</label>
      <p className='text-xs text-gray-500 mb-1'>
        Available placeholders: [name], [token], [expireDate]
      </p>
      <textarea
        className='border rounded p-2 w-full text-sm font-mono'
        rows={16}
        value={body}
        onChange={(e) => { setBody(e.target.value); setDirty(true) }}
      />
      <div className='flex gap-2 mt-3'>
        <Button onClick={handleSave} disabled={!dirty}>Save</Button>
        <Button onClick={handleCopy} className={copied ? 'bg-green-600 hover:bg-green-700' : ''}>
          {copied ? 'Copied' : 'Copy to Clipboard'}
        </Button>
      </div>
    </div>
  )
}

const EmailTemplatesPage = () => {
  const { showToast } = useToast()
  const [templates, setTemplates] = useState(DEFAULT_TEMPLATES)
  const [loading, setLoading] = useState(true)
  const db = getDatabase()

  useEffect(() => {
    const templatesRef = ref(db, 'EmailTemplates')
    const unsubscribe = onValue(templatesRef, (snapshot) => {
      const val = snapshot.val()
      if (val) {
        setTemplates({ ...DEFAULT_TEMPLATES, ...val })
      }
      setLoading(false)
    })
    return () => unsubscribe()
  }, [db])

  const handleSave = async (key, template) => {
    try {
      await set(ref(db, `EmailTemplates/${key}`), template)
      showToast('Template saved', 'green')
    } catch (err) {
      console.error(err)
      showToast('Failed to save template', 'red')
    }
  }

  if (loading) return <div className='p-4'>Loading templates...</div>

  return (
    <div className='p-4 max-w-4xl mx-auto'>
      <h1 className='text-4xl mb-4'>Settings</h1>
      <p className='text-sm text-gray-600 mb-4'>
        Edit the email templates below. Use placeholders like [name], [token], and [expireDate] which will be replaced when copying.
      </p>
      {Object.entries(templates).map(([key, template]) => (
        <TemplateEditor
          key={key}
          templateKey={key}
          template={template}
          onSave={handleSave}
        />
      ))}
    </div>
  )
}

export default EmailTemplatesPage
